import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { DefaultAutocomplete } from "../../../../../../common/components/default_autocomplete";
import { FormTimeMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_time_masked_input";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import dayjs from "dayjs";
import { FuncionarioTreinamento } from "../../../../../rh/domain/models/funcionario_treinamento";
import { TreinamentoForm, TreinamentoSchema } from "../../../../../rh/presentation/schemas/Funcionarios_cadastro/treinamento_schema";
import { TreinamentoDropDown } from "../../../../../rh/domain/models/treinamento_dropdown";

/**
 * CadastroTreinamentoModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedTreinamento: FuncionarioTreinamento | null,
  treinamentoLista: TreinamentoDropDown[],
  onSubmit: (form: TreinamentoForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
};

export const CadastroTreinamentoModal = ({
  open,
  saving,
  selectedTreinamento,
  treinamentoLista,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const [localTreinamentoLista, setLocalTreinamentoLista] = useState<TreinamentoDropDown[]>([]);
  const hookForm = useForm<TreinamentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(TreinamentoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedTreinamento) {
        const treinamento = treinamentoLista.find((x) => x.idTreinamento === selectedTreinamento.idTreinamento);
        if (!treinamento) {
          setLocalTreinamentoLista([...treinamentoLista, {
            idTreinamento: selectedTreinamento.idTreinamento,
            descricao: selectedTreinamento.treinamento,
            cargaHoraria: selectedTreinamento.cargaHoraria,
          }]);
        } else {
          setLocalTreinamentoLista(treinamentoLista);
        }

        if (selectedTreinamento.dataInicio) {
          hookForm.setValue('dataRealização', selectedTreinamento.dataInicio);
        } else {
          hookForm.resetField('dataRealização');
        }

        hookForm.setValue('idTreinamento', selectedTreinamento.idTreinamento);
        hookForm.setValue('cargaHoraria', selectedTreinamento.cargaHoraria);
        hookForm.setValue('local', selectedTreinamento.local);
        hookForm.setValue('instrutor', selectedTreinamento.instrutor);
        hookForm.setValue('prazoAvaliacao', selectedTreinamento.prazo);
      } else {
        setLocalTreinamentoLista(treinamentoLista);
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    open,
    selectedTreinamento,
    treinamentoLista,
  ]);

  const handleSubmit = useCallback((form: TreinamentoForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleChangeHistory = useCallback(() => {
    if (selectedTreinamento) {
      onOpenChangeHistory(selectedTreinamento.idFuncionarioTreinamento, LogTipoTabela.FUNCIONARIO_TERC_TREINAMENTO);
    }
  }, [selectedTreinamento, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "35rem",
          },
        },
      }}>
      <DialogTitle>
        {selectedTreinamento ? "Editar" : "Cadastrar"} treinamento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <Controller
                name='idTreinamento'
                control={hookForm.control}
                render={({ field }) => (
                  <DefaultAutocomplete
                    label="Treinamento"
                    newButtonLabel="Novo treinamento"
                    getOptionKey={(item) => item.idTreinamento}
                    getOptionLabel={(item) => item.descricao}
                    required={true}
                    readOnly={saving}
                    value={field.value ? localTreinamentoLista.find((x) => x.idTreinamento === field.value) : null}
                    options={localTreinamentoLista}
                    onNew={() => { }}
                    onChange={(newValue) => {
                      const treinamentoCargaHoraria = localTreinamentoLista.find((x) => x.idTreinamento === newValue?.idTreinamento)?.cargaHoraria;
                      hookForm.setValue('cargaHoraria', treinamentoCargaHoraria ?? selectedTreinamento?.cargaHoraria ?? 0);
                      field.onChange(newValue?.idTreinamento)
                    }} />
                )} />

              <FormTimeMaskedInput
                name='cargaHoraria'
                label='Carga horária Mensal'
                readOnly={true}
                required={true}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='local'
                label='Local'
                readOnly={saving}
                required={true}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='instrutor'
                label='Instrutor'
                readOnly={saving}
                required={true}
                reactHookForm={hookForm}
              />

              <Controller
                name='dataRealização'
                control={hookForm.control}
                render={({ field }) => (
                  <DatePicker
                    readOnly={saving}
                    slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
                    label='Data Realização'
                    value={field.value ? dayjs.utc(field.value as any).local().startOf('day') : null}
                    onChange={value => {
                      if (value === null || value.isValid()) field.onChange(value?.startOf('day').toDate() ?? null);
                    }} />
                )} />

              <FormMaskedInput
                name='prazoAvaliacao'
                label='Prazo para Avaliar'
                mask='000'
                readOnly={saving}
                required={true}
                maskPlaceholder='___'
                reactHookForm={hookForm} />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedTreinamento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}
